// Simulation events (§17.4). Frozen after WP0: new event types are appended only.
import type { World } from './types';

export interface SimEvent { type: number; tick: number; a: number; b: number; c: number; d: number; x: number; y: number }
// a,b: slots (u8; NO_SLOT=255); c,d: int16 payloads; x,y: meters (encoded cm)

export const EV = {
  LUNGE: 1, HIT: 2, CLASH: 3, GRAB: 4, GRAB_MISS: 5, THROW: 6, ESCAPE: 7, JUMP: 8, LAND: 9,
  TEETER: 10, TEETER_SAVE: 11, LEDGE: 12, CLIMB: 13, STOMP: 14, FALL: 15, KO: 16, BALLOON_SAVE: 17,
  ITEM_GET: 18, ITEM_USE: 19, CRATE_DROP: 20, CRATE_OPEN: 21, PEEL_SLIP: 22, BOMB_LAND: 23, BOMB_BOOM: 24,
  PLUNGER_HIT: 25, SHAKE_START: 26, SHAKE_END: 27, PUNCH_WINDUP: 28, PUNCH: 29, FLOAT_START: 30, FLOAT_POP: 31,
  PEBBLE_DROP: 32, PEBBLE_HIT: 33, HONK: 34, EMOTE: 35, PHASE: 36, HAZARD_WARN: 37, HAZARD_FIRE: 38,
  TILE: 39, WEDGE: 40, CROWN_POP: 41, CROWN_PICK: 42, CROWN_LOST: 43, POTATO_NEW: 44, POTATO_PASS: 45,
  POTATO_BOOM: 46, RESPAWN: 47, MATCH_POINT: 48, ROUND_OVER: 49, BUMPER: 50, SPUN: 51, FLUNG: 52,
  DUMMY_HIT: 53, GHOST_READY: 54, COUNTDOWN: 55, DETHRONE: 56, READY_PAD: 57, VEND: 58, HEAD_BONK: 59,
  // Additive (INT-A): a lost crown drops back in at its spawn point (it used to be a CROWN_POP with no slot).
  CROWN_RESPAWN: 60,
} as const;
export type EV = (typeof EV)[keyof typeof EV];

/** Field meaning per type (documentation + debug printing). */
export const EVENT_DEFS: Record<number, string> = {
  1: 'a=slot',
  2: 'a=attacker b=victim c=Cause d=kb*10 x,y=contact',
  3: 'a,b=slots x,y=midpoint',
  4: 'a=carrier b=victim', 5: 'a=slot', 6: 'a=carrier b=victim', 7: 'a=victim b=carrier',
  8: 'a=slot', 9: 'a=slot d=impactSpeed*10 x,y=landing point',
  10: 'a=slot x,y=edgePoint', 11: 'a=slot c=1 if CLUTCH', 12: 'a=slot x,y=edgePoint', 13: 'a=slot', 14: 'a=stomper b=victim',
  15: 'a=victim b=creditSlot c=Cause', 16: 'a=victim b=credit c=Cause d=flags(1 revenge,2 bounty,4 self,8 ghostKo,16 final,32 teamkill,64 double) x,y=splash',
  17: 'a=slot', 18: 'a=slot b=ItemKind', 19: 'a=slot b=ItemKind', 20: 'c=entId x,y', 21: 'a=slot c=entId b=ItemKind',
  22: 'a=victim b=owner c=peelAgeTicks', 23: 'c=entId x,y', 24: 'a=owner c=entId x,y', 25: 'a=owner b=target', 26: 'a=slot', 27: 'a=slot',
  28: 'a=slot', 29: 'a=slot', 30: 'a=slot', 31: 'a=slot', 32: 'a=ghost x,y', 33: 'a=ghost b=victim',
  34: 'a=slot b=honkId(cosmetic resolved client-side, 0)', 35: 'a=slot b=emote(1-4)', 36: 'b=RoundPhase',
  37: 'b=hazardKind c=index d=leadTicks x,y', 38: 'b=hazardKind c=index x,y', 39: 'a=triggering slot or 255, c=tile b=TileState', 40: 'c=wedge b=0 warn|1 fall',
  41: 'a=fromSlot x,y', 42: 'a=slot', 43: '', 44: 'a=slot', 45: 'a=from b=to', 46: 'a=victim b=credit x,y',
  47: 'a=slot x,y', 48: '', 49: 'a=winner(255 draw, 254 team) b=winningTeam', 50: 'a=slot c=bumper', 51: 'a=slot', 52: 'a=slot',
  53: 'c=entId d=dizzy', 54: 'a=slot', 55: 'b=3|2|1|0', 56: 'a=champion b=culprit', 57: 'a=slot b=1 ready', 58: 'a=slot b=ItemKind', 59: 'a=victim',
  60: 'x,y=spawn point (the crown drops in from 4 m above it)',
};

export const HazardKind = { FLIPPER: 1, PINBALL_LAUNCH: 2, WEDGE: 3, CANDLE: 4, SWEEPER_REVERSE: 5, THIRD_ARM: 6, BUBBLE: 7, TILE_SD: 8 } as const;
export type HazardKind = (typeof HazardKind)[keyof typeof HazardKind];

/** Bits of the KO event's `d` field. */
export const KoFlag = { REVENGE: 1, BOUNTY: 2, SELF: 4, GHOST_KO: 8, FINAL: 16, TEAMKILL: 32, DOUBLE: 64 } as const;
export type KoFlag = (typeof KoFlag)[keyof typeof KoFlag];

export function emit(w: World, type: number, a = 255, b = 255, c = 0, d = 0, x = 0, y = 0): void {
  w.events.push({ type, tick: w.tick, a, b, c, d, x, y });
}
